import { List, Map } from 'immutable';
import {
    isEmpty,
    isUndefined,
    map,
    forEach,
    find,
    filter,
    sortBy,
    values,
    isObjectLike,
    isArrayLikeObject,
} from 'lodash';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { IMAGE_STATUS } from 'commons-vehicle/ModelYear/constants/exteriorColourStatuses.js';

import ModelTrimConfigurationModel from 'commons-vehicle/ModelYear/models/ModelTrimConfigurationModel.js';
import * as UtilitiesService from 'commons-vehicle/ModelYear/services/DataUtilitiesService/DataUtilitiesService.js';
import { resetPurchaseMethodsFromOriginData } from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';
import {
    getIconFromFeatureCode,
    getFeatureIcons,
} from 'commons-vehicle/ModelYear/services/ModelTrimItemsService/ModelTrimItemsService.js';

import { resetEngineFromConfigurationData } from 'commons-vehicle/ModelYear/services/EngineService/EngineService.js';
import { resetTransmissionFromConfigurationData } from 'commons-vehicle/ModelYear/services/TransmissionService/TransmissionService.js';
import { resetColourFromConfigurationData } from 'commons-vehicle/ModelYear/services/ColourService/ColourService.js';

import { MODEL_CONFIGURATION } from 'commons-vehicle/constants/dataConversions.js';
import { DEFAULT_KEY_ORDER, SYSTEM_PACKAGE_CODE } from 'commons-vehicle/constants/dataTypes.js';
/* eslint import/prefer-default-export: 0 no-bitwise: 0 */

function getConfigurationColours(configurationColoursData, features) {
    const colours = {};
    forEach(configurationColoursData, (configurationColourData) => {
        const colour = resetColourFromConfigurationData(configurationColourData, features);
        if (colour.colourCode !== '') {
            colours[colour.colourCode] = colour;
        }
    });

    return colours;
}

function getConfigurationFeaturesAndOptions(
    configurationFeatureCodesData,
    modelFeaturesAndOptions,
    configurationTrimsBitField
) {
    const configurationFeaturesAndOptions = {
        modelTrimFeatures: {},
        features: {},
        options: {},
    };

    if (
        isEmpty(configurationFeatureCodesData) ||
        isEmpty(modelFeaturesAndOptions) ||
        !isObjectLike(modelFeaturesAndOptions) ||
        (!configurationTrimsBitField.trimsBitField && isEmpty(configurationTrimsBitField.trimsBitFields))
    ) {
        return configurationFeaturesAndOptions;
    }

    const currentFeaturesAndOptions = find(modelFeaturesAndOptions, (featuresAndOptions) => {
        return UtilitiesService.intersectModelTrimsBitFieldValue(featuresAndOptions, configurationTrimsBitField) > 0;
    });
    configurationFeaturesAndOptions.options = currentFeaturesAndOptions.options;

    if (
        currentFeaturesAndOptions &&
        (!isEmpty(currentFeaturesAndOptions.options) || !isEmpty(currentFeaturesAndOptions.features))
    ) {
        forEach(configurationFeatureCodesData, (featureCode) => {
            if (!isUndefined(currentFeaturesAndOptions.options[featureCode])) {
                configurationFeaturesAndOptions.features[featureCode] = currentFeaturesAndOptions.options[featureCode];

                configurationFeaturesAndOptions.features[featureCode].rank =
                    currentFeaturesAndOptions.options[featureCode].keyOrder === 0
                        ? DEFAULT_KEY_ORDER
                        : currentFeaturesAndOptions.options[featureCode].keyOrder;
            } else if (!isUndefined(currentFeaturesAndOptions.features[featureCode])) {
                configurationFeaturesAndOptions.modelTrimFeatures[featureCode] =
                    currentFeaturesAndOptions.features[featureCode];

                configurationFeaturesAndOptions.modelTrimFeatures[featureCode].rank =
                    currentFeaturesAndOptions.features[featureCode].keyOrder === 0
                        ? DEFAULT_KEY_ORDER
                        : currentFeaturesAndOptions.features[featureCode].keyOrder;
            }
        });
    }

    return configurationFeaturesAndOptions;
}

function getConfigurationFeatureRankedIcons(configuration, configurationFeatureIcons) {
    const configFeatureRankedIcons = [];
    forEach(configurationFeatureIcons, (iconType, iconTypeKey) => {
        forEach(iconType.featureCodes, (iconFeatureCode) => {
            configFeatureRankedIcons.push(
                Map(
                    getIconFromFeatureCode(
                        configuration.features,
                        configuration.modelTrimFeatures,
                        iconType,
                        iconTypeKey,
                        iconFeatureCode
                    )
                )
            );
        });
    });

    return sortBy(configFeatureRankedIcons, (rankedIcon) => {
        return rankedIcon.rank;
    });
}

export function resetModelTrimConfigurationFromModelData(
    modelTrimConfiguration,
    modelOptions,
    modelTrim,
    driveTrains,
    cabSizes,
    boxSizes
) {
    if (
        isEmpty(modelTrimConfiguration) ||
        !isObjectLike(modelTrimConfiguration) ||
        isEmpty(modelTrim) ||
        !isObjectLike(modelTrim) ||
        isEmpty(driveTrains) ||
        !isArrayLikeObject(driveTrains)
    ) {
        return new ModelTrimConfigurationModel();
    }

    const { trimsBitField, trimsBitFields } = UtilitiesService.getModelTrimsBitField(
        modelTrimConfiguration[MODEL_CONFIGURATION.BIT_FIELD]
    );

    const configID = modelTrimConfiguration[MODEL_CONFIGURATION.ID];
    const configStringID = (configID.length === 1 ? '0' : '') + configID;
    const configModelTrimCode = modelTrimConfiguration[MODEL_CONFIGURATION.TRIM_CODE];
    const configPackageCode = modelTrimConfiguration[MODEL_CONFIGURATION.PACKAGE_CODE];
    const configTrimsBitField = {
        trimsBitField,
        trimsBitFields,
    };

    const featuresAndOptions = getConfigurationFeaturesAndOptions(
        modelTrimConfiguration[MODEL_CONFIGURATION.OPTION_CODES],
        modelOptions,
        configTrimsBitField
    );

    const configFeatureIcons = getFeatureIcons(featuresAndOptions.features, featuresAndOptions.modelTrimFeatures);
    if (!isEmpty(configFeatureIcons) && !isEmpty(featuresAndOptions.modelTrimFeatures)) {
        forEach(featuresAndOptions.modelTrimFeatures, (feature) => {
            map(feature.iconTypes, (iconType, key) => {
                const newIconType = iconType;
                if (
                    !isEmpty(configFeatureIcons[key]) &&
                    newIconType.featureCode !== configFeatureIcons[key].featureCode
                ) {
                    newIconType.isInvalid = true;
                }
            });
        });
    }

    const featureIcons = getConfigurationFeatureRankedIcons(featuresAndOptions, configFeatureIcons);
    const nonModelTrimFeatureIcons = filter(featureIcons, (featureIcon) => {
        return !featureIcon.isFromModelTrim;
    });

    const exteriorColourStatus = isUndefined(modelTrimConfiguration[MODEL_CONFIGURATION.EXTERIOR_COLOUR_STATUS])
        ? IMAGE_STATUS.NO_IMAGE
        : modelTrimConfiguration[MODEL_CONFIGURATION.EXTERIOR_COLOUR_STATUS];
    const driveTrain = UtilitiesService.getObjectFromAvailabilityFlag(driveTrains, configTrimsBitField);
    const driveTrainSelectionCode = driveTrain === null ? '' : driveTrain.selectionCode;
    const cabSize = UtilitiesService.getObjectFromAvailabilityFlag(cabSizes, configTrimsBitField);
    const cabSizeSelectionCode = cabSize === null ? '' : cabSize.selectionCode;
    const boxSize = UtilitiesService.getObjectFromAvailabilityFlag(boxSizes, configTrimsBitField);
    const boxSizeSelectionCode = boxSize === null ? '' : boxSize.selectionCode;

    const configurationDescription = UtilitiesService.getDescriptionData(
        modelTrimConfiguration[MODEL_CONFIGURATION.DESCRIPTION]
    );
    return new ModelTrimConfigurationModel({
        trimsBitField,
        trimsBitFields,
        selectionCode: `${configModelTrimCode}|${configStringID}`,
        enabled: true,
        visible: true,
        selected: false,
        userSelected: false,
        code: `${configPackageCode}|${configStringID}`,
        description: configurationDescription,
        id: configID,
        rank: modelTrimConfiguration[MODEL_CONFIGURATION.RANK],
        modelTrimCode: configModelTrimCode,
        modelTrimCodeId: `${configModelTrimCode}|${configID}`,
        modelTrimRank: modelTrimConfiguration[MODEL_CONFIGURATION.TRIM_RANK],
        packageSelectionCode: configPackageCode,
        packageCode: configPackageCode.substring(0, 3) === SYSTEM_PACKAGE_CODE ? '' : configPackageCode,
        modelTrim,
        package: {
            code: configPackageCode.substring(0, 3) === SYSTEM_PACKAGE_CODE ? '' : configPackageCode,
            description: configurationDescription,
        },
        cabSizeSelectionCode,
        cabSize,
        boxSizeSelectionCode,
        boxSize,
        driveTrainSelectionCode,
        driveTrain,
        engine: resetEngineFromConfigurationData(modelTrimConfiguration[MODEL_CONFIGURATION.ENGINE]),
        transmission: resetTransmissionFromConfigurationData(modelTrimConfiguration[MODEL_CONFIGURATION.TRANSMISSION]),
        purchaseMethods: resetPurchaseMethodsFromOriginData(
            DataOrigins.MODEL_YEAR_POWERS_TRIMS,
            modelTrimConfiguration
        ),
        seating: modelTrimConfiguration[MODEL_CONFIGURATION.SEATING],
        fuelCity: modelTrimConfiguration[MODEL_CONFIGURATION.FUEL_CONSUMPTION][0],
        fuelHighway: modelTrimConfiguration[MODEL_CONFIGURATION.FUEL_CONSUMPTION][1],
        modelTrimFeatures: featuresAndOptions.modelTrimFeatures,
        features: featuresAndOptions.features,
        featureIcons: List(featureIcons),
        nonModelTrimFeatureIconsCount: nonModelTrimFeatureIcons.length,
        exteriorColourStatus,
        exteriorColours: List(
            values(
                getConfigurationColours(
                    modelTrimConfiguration[MODEL_CONFIGURATION.URL_EXTERIOR_COLOUR],
                    featuresAndOptions.options
                )
            )
        ),
        interiorColours: List(
            values(
                getConfigurationColours(
                    modelTrimConfiguration[MODEL_CONFIGURATION.URL_INTERIOR_TRIM],
                    featuresAndOptions.options
                )
            )
        ),
        smNewTrimCarId: modelTrimConfiguration[MODEL_CONFIGURATION.CAR_ID],
        smNewCarId: modelTrimConfiguration[MODEL_CONFIGURATION.VEH_ID],
        configurationItemsAreLoaded: false,
    });
}

export default {
    getConfigurationColours,
    getConfigurationFeaturesAndOptions,
    getConfigurationFeatureRankedIcons,
    resetModelTrimConfigurationFromModelData,
};



// WEBPACK FOOTER //
// ./src/ModelYear/services/ModelTrimConfigurationService/ModelTrimConfigurationService.js